import { ServiceType } from './types';

export interface RequestTableColumn {
    /** Key into the table Row; also the sort key. */
    id: string;
    /** Translation id. Resolved by the caller, so this module stays free of JSX and testable. */
    labelId: string;
}

const col = (id: string, labelId: string): RequestTableColumn => ({ id, labelId });

// One const per (id, labelId) pair, NOT per id. Two columns share an id across tables while
// carrying different headings — Surgery's `service` reads "Procédure" and its `dateAdded` reads
// "Date ajouté", where the service tables read "Examens demandés" and "Date". Folding each pair
// into a single const by id would silently retitle one of the two tables, which is precisely the
// cross-module breakage this module exists to prevent.
const ID = col('id', 'pages.hospital.services.request.request_id');
const REQUESTER = col('requester', 'pages.hospital.services.request.requester');
/** SHER-722: who wrote the result (the result submission's author), never the requester. */
const REPORTER = col('reporter', 'hospital.request.reporting_doctor');
/** SHER-910: where the request came FROM, from the snapshot taken at creation. */
const ORIGIN = col('requesterDepartment', 'hospital.departments.requester_department');
/**
 * `departmentRequest`. NOT a destination on the request-form path — SHER-446 pre-fills that form
 * from the requesting doctor's own service — despite the name the API entity gives it. See
 * SHER-1036 before trusting it, and `docs/features/medical-record-requests/decisions/`
 * for why it left the service tables.
 */
const DESTINATION = col('department', 'hospital.departments.department');
const SERVICE = col('service', 'hospital.request.services');
const DATE = col('dateAdded', 'hospital.request.request_datetime');
const REQUESTED_SAMPLING = col('requestedSampling', 'hospital.request.requested_sampling.legend');
const STATUS = col('status', 'pages.hospital.services.status');

const MAIN_SURGEON = col('mainSurgeon', 'pages.hospital.services.main_surgeon');
const SURGERY_PROCEDURE = col('service', 'pages.hospital.services.surgery');
const CIM11 = col('cim11', 'pages.hospital.services.request.surgery_indication');
const DATE_ADDED = col('dateAdded', 'general.date_added');
const DATE_COMPLETED = col('dateCompleted', 'hospital.request.dateCompleted');
const TOTAL_DAYS = col('totalDays', 'hospital.request.total_days');

/**
 * Laboratory, Radiology and anything else that has not asked for its own list.
 *
 * SHER-910: ONE department column, the origin. Cordonnerie, Laboratory and Radiology all render
 * this list; on them the two department columns answered the same question twice.
 */
const SERVICES_COLUMNS = [ID, REQUESTER, ORIGIN, REPORTER, SERVICE, DATE, STATUS];
const LAB_COLUMNS = [ID, REQUESTER, ORIGIN, REPORTER, SERVICE, DATE, REQUESTED_SAMPLING, STATUS];
/** SHER-722: Cordonnerie delivers a product, not a clinical result, so it has no report author. */
const SHOE_COLUMNS = [ID, REQUESTER, ORIGIN, SERVICE, DATE, STATUS];

/**
 * Only the tables that genuinely diverge; everything else falls back to SERVICES_COLUMNS.
 *
 * A lookup rather than a chain of conditionals, so the next divergence is a new key instead of
 * another `!==` buried inside a list several service types share — the shape SHER-908 documented
 * when it hit the same trap elsewhere in RequestTable.tsx.
 */
const COLUMNS_BY_SERVICE_TYPE: Partial<Record<ServiceType, RequestTableColumn[]>> = {
    [ServiceType.LABORATORY]: LAB_COLUMNS,
    [ServiceType.SHOE]: SHOE_COLUMNS,
    [ServiceType.SURGERY]: [
        MAIN_SURGEON, DESTINATION, SURGERY_PROCEDURE, CIM11, DATE_ADDED, DATE_COMPLETED, TOTAL_DAYS, STATUS,
    ],
};

/** Identity columns, prepended when the table is not already scoped to a single patient. */
const patientColumns = (healthIdLabelId: string): RequestTableColumn[] => [
    col('nhc', healthIdLabelId),
    col('patient', 'general.patient'),
    col('dob', 'investigation.create.personal_data.short-fields.birthdate'),
];

export function buildRequestTableColumns(
    requestServiceType: ServiceType,
    withPatientColumns: boolean,
    healthIdLabelId: string,
): RequestTableColumn[] {
    const base = COLUMNS_BY_SERVICE_TYPE[requestServiceType] ?? SERVICES_COLUMNS;

    // Always a copy: `base` is a module-level array, and handing it out would let any caller
    // mutate every future table. The code this replaced spliced its head cells in place.
    return withPatientColumns ? [...patientColumns(healthIdLabelId), ...base] : [...base];
}
